import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";

function getVisibleSteps(steps, insuranceType, termsStepId) {
  const isTripSecure = insuranceType === "tripsecure";

  if (isTripSecure) {
    return steps.filter((step) => step.id !== termsStepId);
  }

  return steps.filter((step) => step.id !== 4 && step.id !== termsStepId);
}

function getScrollParent(node) {
  let parent = node?.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") return parent;
    parent = parent.parentElement;
  }
  return null;
}

export function StepperNav({
  steps,
  currentStep,
  goToStep,
  insuranceType,
  termsStepId,
}) {
  const visibleSteps = getVisibleSteps(steps, insuranceType, termsStepId);
  const [isStuck, setIsStuck] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const scrollParent = getScrollParent(nav);
    const updateStuck = () => {
      const navTop = nav.getBoundingClientRect().top;
      const parentTop = scrollParent
        ? scrollParent.getBoundingClientRect().top
        : 0;
      setIsStuck(navTop <= parentTop + 1);
    };

    updateStuck();
    const target = scrollParent || window;
    target.addEventListener("scroll", updateStuck, { passive: true });
    window.addEventListener("resize", updateStuck);

    return () => {
      target.removeEventListener("scroll", updateStuck);
      window.removeEventListener("resize", updateStuck);
    };
  }, []);

  const currentIndex = Math.max(
    0,
    visibleSteps.findIndex((step) => step.id === currentStep)
  );
  const progress =
    visibleSteps.length > 1 ? currentIndex / (visibleSteps.length - 1) : 0;
  const inset = 100 / visibleSteps.length / 2;

  return (
    <nav
      ref={navRef}
      aria-label="Policy creation steps"
      className={`sticky top-0 z-30 w-full transition-all duration-300 ease-out ${
        isStuck
          ? "bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-[0_8px_20px_rgba(15,23,42,0.08)]"
          : "bg-transparent"
      }`}
    >
      <div
        className={`px-4 sm:px-6 lg:px-8 transition-all duration-300 ${
          isStuck ? "py-2.5" : "py-3"
        }`}
      >
        <div className="relative w-full">
          <div
            className="absolute top-[13px] sm:top-[15px] h-[2px] rounded-full bg-slate-200"
            style={{ left: `${inset}%`, right: `${inset}%` }}
            aria-hidden="true"
          />
          <div
            className="absolute top-[13px] sm:top-[15px] h-[2px] rounded-full bg-emerald-500 transition-all duration-500 ease-out"
            style={{
              left: `${inset}%`,
              width: `calc(${progress * (100 - inset * 2)}%)`,
            }}
            aria-hidden="true"
          />
          <ol
            className="relative grid w-full"
            style={{
              gridTemplateColumns: `repeat(${visibleSteps.length}, minmax(0, 1fr))`,
            }}
          >
            {visibleSteps.map((step) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              const canAccess = step.id <= currentStep;

              return (
                <li
                  key={`step-nav-${step.id}`}
                  className="relative z-10 flex justify-center"
                >
                  <button
                    type="button"
                    onClick={() => canAccess && goToStep(step.id)}
                    disabled={!canAccess}
                    aria-current={isActive ? "step" : undefined}
                    className={`group flex min-w-0 flex-col items-center gap-1.5 px-1 transition-transform duration-200 ${
                      canAccess ? "cursor-pointer" : "cursor-not-allowed"
                    } ${canAccess && !isActive ? "hover:-translate-y-0.5" : ""}`}
                  >
                    <span
                      className={`flex h-7 w-7 items-center justify-center rounded-full border-2 transition-all duration-300 sm:h-8 sm:w-8 ${
                        isCompleted
                          ? "border-emerald-500 bg-emerald-500 text-white shadow-sm shadow-emerald-500/30"
                          : isActive
                          ? "border-emerald-500 bg-white text-emerald-600 ring-4 ring-emerald-500/15"
                          : "border-slate-200 bg-white text-slate-400"
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={2.5} />
                      ) : (
                        <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      )}
                    </span>
                    <span
                      className={`block max-w-[5.5rem] truncate text-center text-[11px] font-semibold sm:max-w-[9rem] sm:text-xs ${
                        isActive
                          ? "text-emerald-700"
                          : isCompleted
                          ? "text-slate-800"
                          : "text-slate-400"
                      }`}
                    >
                      {step.title}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </nav>
  );
}
